export type AmountMode = 'percent' | 'flat' | 'off';

export type InvoiceTheme = 'classic' | 'slate';

export interface InvoiceLabels {
  title: string;
  billTo: string;
  shipTo: string;
  date: string;
  paymentTerms: string;
  dueDate: string;
  poNumber: string;
  item: string;
  quantity: string;
  rate: string;
  amount: string;
  notes: string;
  terms: string;
  subtotal: string;
  tax: string;
  discount: string;
  shipping: string;
  total: string;
  amountPaid: string;
  balanceDue: string;
}

export interface InvoiceLine {
  description: string;
  details?: string;
  qty: number;
  unit: string;
  unitPrice: number;
  vatPercent: number;
}

export interface InvoiceDraft {
  id: string;
  number: string;
  /** Set once when the invoice is first saved; used for list ordering. */
  createdAt?: string;
  updatedAt: string;

  /** Custom logo as data URL; empty = placeholder */
  logoUrl: string;
  /** Editable document title (e.g. INVOICE) */
  titleEn: string;
  titleFi: string;
  labels: InvoiceLabels;
  theme: InvoiceTheme;
  shipTo: string;
  poNumber: string;
  /** Free-text payment terms */
  paymentTerms: string;
  notes: string;
  terms: string;
  amountPaid: number;
  taxMode: AmountMode;
  taxValue: number;
  discountMode: AmountMode;
  discountValue: number;
  shippingMode: 'flat' | 'off';
  shippingValue: number;
  showDiscount: boolean;
  showShipping: boolean;
  showTax: boolean;

  // Bill To (customer) — shown first on invoice
  customerId?: string;
  toName: string;
  toCompany: string;
  toAddress: string;
  toPhone: string;

  // From (seller contact) — independent of footer / slip
  fromName: string;
  fromCompany: string;
  fromAddress: string;

  // Meta
  date: string;
  paymentTermsDays: number;
  dueDate: string;
  lateInterestPercent: number;
  reference: string;
  customerBusinessId: string;
  companyBusinessId: string;
  companyVatId: string;

  // Company / bank (footer + payment slip)
  companyName: string;
  companyAddress: string;
  companyTel: string;
  companyEmail: string;
  companyWww: string;
  iban: string;
  bic: string;

  /** Signature image as data URL (payment slip) */
  signatureUrl: string;
  /** Payer on payment slip — independent of Bill To customer */
  payerName: string;
  payerCompany: string;
  payerAddress: string;
  /** Recipient on payment slip — independent of sender / footer */
  slipCompanyName: string;
  slipCompanyAddress: string;
  /** Payer account number on payment slip */
  fromAccount: string;

  items: InvoiceLine[];
  currency: string;
}

export interface InvoiceTemplate {
  fromName: string;
  fromAddress: string;
  companyName: string;
  companyAddress: string;
  companyTel: string;
  companyEmail: string;
  companyWww: string;
  companyBusinessId: string;
  companyVatId: string;
  iban: string;
  bic: string;
  lateInterestPercent: number;
  logoUrl?: string;
  titleEn?: string;
  titleFi?: string;
}

export interface InvoiceSettings {
  nextInvoiceNumber: number;
  dismissedBanners: string[];
}

/** Saved Bill-To contact (IndexedDB guest / Firestore when signed in). */
export interface Customer {
  id: string;
  name: string;
  email: string;
  /** Optional company / organization name */
  company: string;
  phone: string;
  /** Bill To address */
  address: string;
  shipTo: string;
  createdAt: string;
  updatedAt: string;
}

/** Local auth account (password stored as PBKDF2 hash only). */
export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  passwordHash: string;
  passwordSalt: string;
  createdAt: string;
  updatedAt: string;
}

/** Browser session keyed by cookie value. */
export interface AuthSession {
  id: string;
  userId: string;
  remember: boolean;
  createdAt: string;
  expiresAt: string;
}

/** One-time password reset token (store hash only). */
export interface PasswordReset {
  id: string;
  userId: string;
  tokenHash: string;
  createdAt: string;
  expiresAt: string;
  usedAt?: string;
}

export type PublicAuthUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Plain sequential invoice number: 1, 2, 3, … */
export function formatInvoiceNumber(seq: number): string {
  const n = Math.max(1, Math.floor(seq) || 1);
  return String(n);
}

/** Parse 12 / #012 / INV-012 → sequence; unknown formats → 0 */
export function parseInvoiceSequence(number: string): number {
  const raw = String(number || '').trim();
  if (/^\d{1,6}$/.test(raw)) return parseInt(raw, 10);
  const hash = /^#(\d{1,6})$/.exec(raw);
  if (hash) return parseInt(hash[1], 10);
  const inv = /^INV-(\d+)$/i.exec(raw);
  if (inv) return parseInt(inv[1], 10);
  return 0;
}
